import * as React from "react";
import { toast } from "sonner";
import { Loader2, Mail, Shield } from "lucide-react";

import { studentApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { createLogger } from "@/utils/logger";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

const logger = createLogger("EditUserDialog");

const GRADUATION_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const DELIVERY_MODES = ["HYBRID", "ONCAMPUS", "ZOOM", "ONLINE"] as const;

type EditForm = {
  name: string;
  email: string;
  cellPhone: string;
  dateOfBirth: string;
  programEnrolled: string;
  deliveryMode: string;
  graduationMonth: string;
  graduationYear: string;
  isBlocked: boolean;
  requiresPasswordReset: boolean;
};

const emptyForm = (): EditForm => ({
  name: "",
  email: "",
  cellPhone: "",
  dateOfBirth: "",
  programEnrolled: "",
  deliveryMode: "",
  graduationMonth: "",
  graduationYear: "",
  isBlocked: false,
  requiresPasswordReset: false,
});

const getUserInitials = (name?: string, email?: string) => {
  const source = (name || email || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const formatDateOfBirth = (rawValue: string | null | undefined): string => {
  if (!rawValue) return "";
  const raw = String(rawValue);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const dob = new Date(raw);
  if (Number.isNaN(dob.getTime())) return "";
  const y = dob.getFullYear();
  const m = String(dob.getMonth() + 1).padStart(2, "0");
  const d = String(dob.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const errorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === "object") {
    const record = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return record.response?.data?.message || record.message || fallback;
  }
  return fallback;
};

export interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  fallbackName?: string;
  fallbackEmail?: string;
  fallbackCellPhone?: string;
  fallbackProgram?: string;
  onSaved?: () => void;
}

export function EditUserDialog({
  open,
  onOpenChange,
  userId,
  fallbackName,
  fallbackEmail,
  fallbackCellPhone,
  fallbackProgram,
  onSaved,
}: EditUserDialogProps) {
  const [form, setForm] = React.useState<EditForm>(emptyForm);
  const [loading, setLoading] = React.useState(false);
  const [processingEdit, setProcessingEdit] = React.useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState("");
  const [processingEmailChange, setProcessingEmailChange] = React.useState(false);

  React.useEffect(() => {
    if (!open || !userId) return;

    let cancelled = false;
    setForm({
      ...emptyForm(),
      name: fallbackName ?? "",
      email: fallbackEmail ?? "",
      cellPhone: fallbackCellPhone ?? "",
      programEnrolled: fallbackProgram ?? "",
    });
    setLoading(true);

    void (async () => {
      try {
        const response = await studentApi.getStudentOverview(userId);
        if (cancelled) return;

        const student = response.data?.student;
        const enrollments = response.data?.enrollments ?? [];
        if (!student) return;

        const fullName =
          student.fullName ||
          (student.firstName && student.lastName
            ? `${student.firstName} ${student.lastName}`
            : fallbackName || "");

        const programEnrolled =
          student.programEnrolled ||
          enrollments[0]?.programName ||
          enrollments[0]?.program ||
          fallbackProgram ||
          "";

        setForm({
          name: fullName,
          email: student.email || fallbackEmail || "",
          cellPhone: student.cellPhoneNumber || fallbackCellPhone || "",
          dateOfBirth: formatDateOfBirth(student.dateOfBirth),
          programEnrolled,
          deliveryMode: student.deliveryMode || "",
          graduationMonth: student.graduation?.month || "",
          graduationYear: student.graduation?.year || "",
          isBlocked: student.isBlocked,
          requiresPasswordReset: student.requiresPasswordReset,
        });
      } catch (error) {
        if (cancelled) return;
        logger.error(
          error instanceof Error
            ? error
            : new Error("Failed to fetch user details for editing"),
          { userId }
        );
        toast.error("Could not load the latest profile. Showing available details.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    userId,
    fallbackName,
    fallbackEmail,
    fallbackCellPhone,
    fallbackProgram,
  ]);

  const updateField = (name: keyof EditForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (
      name === "name" ||
      name === "cellPhone" ||
      name === "dateOfBirth" ||
      name === "programEnrolled" ||
      name === "graduationYear"
    ) {
      updateField(name, value);
    }
  };

  const handleSaveEdit = async () => {
    if (!userId) return;

    setProcessingEdit(true);
    try {
      await studentApi.updateStudent(userId, {
        fullName: form.name,
        cellPhoneNumber: form.cellPhone,
        dateOfBirth: form.dateOfBirth,
        programEnrolled: form.programEnrolled || null,
        ...(form.deliveryMode ? { deliveryMode: form.deliveryMode } : {}),
        ...(form.graduationMonth || form.graduationYear
          ? {
              graduation: {
                month: form.graduationMonth || undefined,
                year: form.graduationYear || undefined,
              },
            }
          : {}),
        isBlocked: form.isBlocked,
        requiresPasswordReset: form.requiresPasswordReset,
      });
      toast.success("User updated successfully");
      onOpenChange(false);
      onSaved?.();
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Failed to update user"));
    } finally {
      setProcessingEdit(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!userId || !newEmail.trim()) return;

    setProcessingEmailChange(true);
    try {
      const response = await studentApi.changeStudentEmail(userId, newEmail.trim());
      toast.success(
        response.message ||
          "Email changed successfully. Student will need to log in again."
      );
      setForm((prev) => ({ ...prev, email: response.data.newEmail }));
      setChangeEmailOpen(false);
      setNewEmail("");
      onSaved?.();
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Failed to change email"));
    } finally {
      setProcessingEmailChange(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[min(90vh,740px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="space-y-0 border-b border-border px-6 py-5 pr-12 text-left">
            <div className="flex items-start gap-3">
              <div
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold tracking-wide text-primary"
                aria-hidden
              >
                {getUserInitials(form.name, form.email)}
              </div>
              <div className="min-w-0 flex-1 space-y-1.5">
                <DialogTitle>Edit user</DialogTitle>
                <DialogDescription className="line-clamp-2">
                  Update profile, program enrollment, and account settings.
                </DialogDescription>
                {form.email ? (
                  <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                    <Mail className="size-3.5 shrink-0" aria-hidden />
                    <span className="truncate">{form.email}</span>
                  </p>
                ) : null}
              </div>
            </div>
          </DialogHeader>

          <div className="relative min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {loading ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : null}
            <div className="grid gap-6">
              <section className="grid gap-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Profile
                </h3>
                <div className="grid gap-2">
                  <Label htmlFor="edit-user-name">Full name</Label>
                  <Input
                    id="edit-user-name"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                    autoComplete="name"
                    disabled={loading}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-user-email">Email</Label>
                  <div className="flex gap-2">
                    <Input
                      id="edit-user-email"
                      name="email"
                      value={form.email}
                      disabled
                      className="min-w-0 flex-1 bg-muted"
                      title="Email cannot be updated here"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setNewEmail(form.email);
                        setChangeEmailOpen(true);
                      }}
                      className="shrink-0"
                      disabled={loading || !userId}
                    >
                      Change
                    </Button>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="edit-user-cellPhone">Cell phone</Label>
                    <Input
                      id="edit-user-cellPhone"
                      name="cellPhone"
                      value={form.cellPhone}
                      onChange={handleInputChange}
                      placeholder="+12125551234"
                      disabled={loading}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-user-dateOfBirth">Date of birth</Label>
                    <Input
                      id="edit-user-dateOfBirth"
                      name="dateOfBirth"
                      type="date"
                      value={form.dateOfBirth}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              </section>

              <Separator />

              <section className="grid gap-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Program
                </h3>
                <div className="grid gap-2">
                  <Label htmlFor="edit-user-programEnrolled">Program enrolled</Label>
                  <Input
                    id="edit-user-programEnrolled"
                    name="programEnrolled"
                    value={form.programEnrolled}
                    onChange={handleInputChange}
                    placeholder="Leave empty to clear"
                    disabled={loading}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-user-deliveryMode">Delivery mode</Label>
                  <Select
                    value={form.deliveryMode || undefined}
                    onValueChange={(deliveryMode) =>
                      updateField("deliveryMode", deliveryMode)
                    }
                    disabled={loading}
                  >
                    <SelectTrigger id="edit-user-deliveryMode" className="w-full">
                      <SelectValue placeholder="Select delivery mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIVERY_MODES.map((mode) => (
                        <SelectItem key={mode} value={mode}>
                          {mode === "HYBRID"
                            ? "Hybrid"
                            : mode === "ONCAMPUS"
                              ? "On-campus"
                              : mode === "ZOOM"
                                ? "Zoom"
                                : "Online"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <p
                    id="edit-user-graduation-label"
                    className="text-sm font-medium leading-none"
                  >
                    Graduation
                  </p>
                  <div
                    className="flex gap-2"
                    role="group"
                    aria-labelledby="edit-user-graduation-label"
                  >
                    <div className="min-w-0 flex-1">
                      <Select
                        value={form.graduationMonth || undefined}
                        onValueChange={(graduationMonth) =>
                          updateField("graduationMonth", graduationMonth)
                        }
                        disabled={loading}
                      >
                        <SelectTrigger id="edit-user-graduationMonth" className="w-full">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {GRADUATION_MONTHS.map((month) => (
                            <SelectItem key={month} value={month}>
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      id="edit-user-graduationYear"
                      name="graduationYear"
                      type="text"
                      inputMode="numeric"
                      value={form.graduationYear}
                      onChange={handleInputChange}
                      placeholder="Year"
                      className="w-[5.5rem] shrink-0"
                      autoComplete="off"
                      disabled={loading}
                    />
                  </div>
                </div>
              </section>

              <Separator />

              <section className="grid gap-3">
                <div className="flex items-center gap-2">
                  <Shield className="size-3.5 text-muted-foreground" aria-hidden />
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Account status
                  </h3>
                </div>
                <div className="grid gap-2">
                  <label
                    htmlFor="edit-user-isBlocked"
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors",
                      form.isBlocked
                        ? "border-destructive/40 bg-destructive/5"
                        : "hover:bg-muted/40"
                    )}
                  >
                    <Checkbox
                      id="edit-user-isBlocked"
                      checked={form.isBlocked}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          isBlocked: checked === true,
                        }))
                      }
                      className="mt-0.5"
                      disabled={loading}
                    />
                    <span className="grid gap-0.5">
                      <span className="text-sm font-medium leading-none">Blocked</span>
                      <span className="text-xs text-muted-foreground">
                        Prevent this student from signing in.
                      </span>
                    </span>
                  </label>
                  <label
                    htmlFor="edit-user-requiresPasswordReset"
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors",
                      form.requiresPasswordReset
                        ? "border-primary/40 bg-primary/5"
                        : "hover:bg-muted/40"
                    )}
                  >
                    <Checkbox
                      id="edit-user-requiresPasswordReset"
                      checked={form.requiresPasswordReset}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          requiresPasswordReset: checked === true,
                        }))
                      }
                      className="mt-0.5"
                      disabled={loading}
                    />
                    <span className="grid gap-0.5">
                      <span className="text-sm font-medium leading-none">
                        Require password reset
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Force a password change on next login.
                      </span>
                    </span>
                  </label>
                </div>
              </section>
            </div>
          </div>

          <DialogFooter className="gap-3 border-t border-border px-6 py-4 sm:gap-3">
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground sm:w-auto"
              onClick={() => onOpenChange(false)}
              disabled={processingEdit}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleSaveEdit()}
              disabled={processingEdit || loading || !userId}
              className="w-full min-w-[10.5rem] sm:w-auto"
            >
              {processingEdit ? (
                <>
                  <Loader2 size={16} className="animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                "Save changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={changeEmailOpen} onOpenChange={setChangeEmailOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Change Student Email</DialogTitle>
            <DialogDescription>
              Update the student&apos;s email address. This will invalidate all
              active sessions and require the student to log in again with the
              new email.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-user-currentEmail" className="text-right">
                Current Email
              </Label>
              <Input
                id="edit-user-currentEmail"
                value={form.email}
                disabled
                className="col-span-3 bg-muted"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-user-newEmail" className="text-right">
                New Email
              </Label>
              <Input
                id="edit-user-newEmail"
                type="email"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                className="col-span-3"
                placeholder="newemail@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setChangeEmailOpen(false);
                setNewEmail("");
              }}
              disabled={processingEmailChange}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleChangeEmail()}
              disabled={
                processingEmailChange ||
                !newEmail.trim() ||
                newEmail.trim() === form.email
              }
              className="flex items-center gap-1"
            >
              {processingEmailChange ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Changing...
                </>
              ) : (
                "Change Email"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
